/* eslint-disable @typescript-eslint/no-explicit-any */
import Card from "@mui/material/Card";
import { useEffect, useRef, useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useCompanyPageStore } from "../../../../../store/companyPageStore";
import {
  SpendQueryType,
  useCompanySpend,
} from "../../../hooks/useCompanySpend";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_worldLow from "@amcharts/amcharts4-geodata/worldLow";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import Stack from "@mui/material/Stack";
import { getNumberLabel } from "../../../../../utils/numberUtils";

am4core.useTheme(am4themes_animated);

export const CompanySpendByCountry = () => {
  const company = useCompanyPageStore((state) => state.company);
  const mapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<am4maps.MapChart | null>(null);

  const {
    data: spendQueryData,
    loading,
    error,
  } = useCompanySpend({
    companyId: company?.hgId,
    queryType: SpendQueryType.BY_COUNTRY,
    // Reasonable number of countries to show on the map
    per: 300,
    skip: !company?.hgId,
  });

  const mapData = useMemo(() => {
    if (!spendQueryData?.data) {
      return [];
    }
    return spendQueryData.data.map((item) => ({
      id: item.country.alpha2,
      name: item.country.name,
      value: item.sumRelativeValues,
    }));
  }, [spendQueryData]);

  useEffect(() => {
    if (!mapRef.current || !mapData.length) {
      // If there's an existing chart and no data, or mapRef is gone, dispose of it.
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
      return;
    }

    // Create map instance
    const chart = am4core.create(mapRef.current, am4maps.MapChart);
    chartRef.current = chart;

    // Set map definition
    chart.geodata = am4geodata_worldLow;

    // Set projection
    chart.projection = new am4maps.projections.Miller();

    // Create map polygon series
    const polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.exclude = ["AQ"];
    polygonSeries.useGeodata = true;
    polygonSeries.nonScalingStroke = true;
    polygonSeries.strokeWidth = 0.5;
    polygonSeries.calculateVisualCenter = true;
    polygonSeries.mapPolygons.template.fill = am4core.color("#E8E8E8");
    polygonSeries.mapPolygons.template.stroke = am4core.color("#FFFFFF");

    // Invalidate image series when polygon series is validated
    polygonSeries.events.on("validated", function () {
      imageSeries.invalidate();
    });

    // Create image series for bubbles
    const imageSeries = chart.series.push(new am4maps.MapImageSeries());
    imageSeries.data = mapData;
    imageSeries.dataFields.value = "value";

    const imageTemplate = imageSeries.mapImages.template;
    imageTemplate.nonScaling = true;

    // Use adapters to position bubbles on country centers
    imageTemplate.adapter.add("latitude", function (latitude, target) {
      const polygon = polygonSeries.getPolygonById(
        (target.dataItem.dataContext as any).id
      );
      if (polygon) {
        return polygon.visualLatitude;
      }
      return latitude;
    });

    imageTemplate.adapter.add("longitude", function (longitude, target) {
      const polygon = polygonSeries.getPolygonById(
        (target.dataItem.dataContext as any).id
      );
      if (polygon) {
        return polygon.visualLongitude;
      }
      return longitude;
    });

    // Create circle for bubbles
    const circle = imageTemplate.createChild(am4core.Circle);
    circle.fillOpacity = 0.8;
    circle.fill = am4core.color("#4285F4");
    circle.stroke = am4core.color("#FFFFFF");
    circle.strokeWidth = 2;

    // Adapter to format the value in the tooltip
    circle.adapter.add("tooltipText", function (_text, target) {
      if (target.dataItem && target.dataItem.dataContext) {
        const dataContext = target.dataItem.dataContext as any;
        if (typeof dataContext.value === "number") {
          const formattedValue = getNumberLabel(dataContext.value, {
            isCurrency: true,
            compact: true,
          });
          return `[bold]${dataContext.name}: ${formattedValue}[/]`;
        }
      }
    });

    // Set up heat rules for bubble sizing
    imageSeries.heatRules.push({
      target: circle,
      property: "radius",
      min: 8,
      max: 30,
      dataField: "value",
    });

    // Add hover effects
    const hoverState = circle.states.create("hover");
    hoverState.properties.scale = 1.2;
    hoverState.properties.fill = am4core.color("#3367D6");

    // Add small map
    chart.smallMap = new am4maps.SmallMap();
    chart.smallMap.series.push(polygonSeries);

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [mapData]);

  const cardContent = useMemo(() => {
    if (loading || !company) {
      return <Skeleton variant="rectangular" width="100%" height={400} />;
    }
    if (error) {
      return (
        <Stack
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height={400}
        >
          <Typography color="error">Error loading data.</Typography>
        </Stack>
      );
    }

    if (mapData.length > 0) {
      return <div ref={mapRef} style={{ width: "100%", height: 400 }} />;
    }

    return (
      <Stack
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={400}
      >
        <Typography variant="body1" color="text.secondary">
          No spend data available for the current selection.
        </Typography>
      </Stack>
    );
  }, [loading, error, mapData, company]);

  return (
    <Box>
      <Card sx={{ p: 3, height: 440 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          Company Spend by Country
        </Typography>
        {cardContent}
      </Card>
    </Box>
  );
};
